/**
 * Search UI - Overlay rendering & event handling with download integration
 */

import { searchMusic } from './search-api.js';
import { DownloadManager } from './download-manager.js';

export class SearchUI {
  constructor(deckId, storage, downloadManager) {
    this.deckId = deckId;
    this.storage = storage;
    this.downloadManager = downloadManager;
    this.searchInput = document.getElementById(`searchPlaylist${deckId}`);
    this.overlay = null;
    this.debounceTimer = null;
    this.currentResults = [];
    this.downloadingTracks = new Set();

    if (this.searchInput) {
      this.createOverlay();
      this.bindEvents();
    }
  }

  /**
   * Create overlay structure above search input
   */
  createOverlay() {
    const overlayDiv = document.createElement('div');
    overlayDiv.className = 'search-overlay';
    overlayDiv.id = `searchOverlay${this.deckId}`;

    this.searchInput.parentElement.style.position = 'relative';
    this.searchInput.parentElement.insertBefore(overlayDiv, this.searchInput);

    this.overlay = overlayDiv;
  }

  /**
   * Bind search input events
   */
  bindEvents() {
    this.searchInput.addEventListener('input', (e) => this.onSearchInput(e));
    this.searchInput.addEventListener('focus', () => {
      if (this.currentResults.length > 0) {
        this.overlay.classList.add('active');
      }
    });
    this.searchInput.addEventListener('blur', () => {
      setTimeout(() => this.overlay.classList.remove('active'), 200);
    });
  }

  /**
   * Handle search input with 500ms debounce
   */
  onSearchInput(event) {
    const query = event.target.value.trim();

    clearTimeout(this.debounceTimer);

    if (query.length < 2) {
      this.overlay.classList.remove('active');
      this.currentResults = [];
      return;
    }

    this.debounceTimer = setTimeout(async () => {
      await this.performSearch(query);
    }, 500);
  }

  /**
   * Perform search & render overlay
   */
  async performSearch(query) {
    try {
      const results = await searchMusic(query);

      if (results.length === 0) {
        this.overlay.classList.remove('active');
        return;
      }

      this.currentResults = results;

      // Cache all results
      for (const result of results) {
        try {
          await this.storage.cacheSearchResult(result);
        } catch (err) {
          console.warn('Failed to cache result:', err);
        }
      }

      this.renderResults(results);
      this.overlay.classList.add('active');
    } catch (err) {
      console.error('Search error:', err);
      this.overlay.classList.remove('active');
    }
  }

  /**
   * Render search results in overlay
   */
  renderResults(results) {
    this.overlay.innerHTML = '';

    results.slice(0, 3).forEach((result) => {
      const resultDiv = document.createElement('div');
      resultDiv.className = 'search-result';
      resultDiv.dataset.sourceId = result.sourceId;

      const imgUrl = result.imageUrl || this.getGradientPlaceholder(result.sourceId);

      resultDiv.innerHTML = `
        <img class="result-thumb" src="${imgUrl}" alt="">
        <div class="result-info">
          <div class="result-title">${this.escapeHtml(result.title)}</div>
          <div class="result-artist">${this.escapeHtml(result.artist)}</div>
        </div>
      `;

      resultDiv.addEventListener('click', () => this.onResultClick(result));
      this.overlay.appendChild(resultDiv);
    });
  }

  /**
   * Handle result click - initiate download and add to playlist
   */
  async onResultClick(result) {
    const trackId = result.sourceId;

    try {
      // Prevent duplicate downloads
      if (this.downloadingTracks.has(trackId)) {
        console.warn('Download already in progress for this track');
        return;
      }

      // Create placeholder track with download status
      const playlistTrack = {
        id: `search_${trackId}_${Date.now()}`,
        name: result.title,
        artist: result.artist,
        album: result.album || '',
        bpm: null,
        key: null,
        energy: 0,
        duration: result.duration || 0,
        audioBuffer: null,
        file: `${result.artist} - ${result.title}.mp3`,
        fileSize: 0,
        addedAt: Date.now(),
        searchSourceId: trackId,
        _isSearchMetadata: true,
        _isDownloading: true,
        _downloadProgress: 0,
        _downloadStatus: 'INITIALIZING', // INITIALIZING, PRE_CHECK, DOWNLOADING, CONVERTING, POST_CHECK, COMPLETE
        _preChecks: [],
        _postChecks: [],
        _analyzing: false,
        _imageUrl: result.imageUrl,
        _preview: result.preview,
      };

      // Add to playlist immediately with download indicator
      const event = new CustomEvent('playlistTrackAdded', {
        detail: {
          deckId: this.deckId,
          track: playlistTrack,
        },
      });
      document.dispatchEvent(event);

      // Mark as added to playlist in cache
      await this.storage.markSearchResultInPlaylist(trackId);

      // Close overlay & clear search
      this.overlay.classList.remove('active');
      this.searchInput.value = '';
      this.currentResults = [];

      // Start download in background
      this.downloadingTracks.add(trackId);
      this.startDownloadProcess(playlistTrack, result);
    } catch (err) {
      console.error('Failed to add track:', err);
    }
  }

  /**
   * Download process with progress tracking and checks
   */
  async startDownloadProcess(playlistTrack, searchResult) {
    const trackId = searchResult.sourceId;

    try {
      // Trigger download with progress callback
      const result = await this.downloadManager.downloadTrackAudio(
        searchResult,
        (progress) => {
          // Update progress in playlist UI
          const event = new CustomEvent('downloadProgress', {
            detail: {
              trackId,
              progress,
              status: playlistTrack._downloadStatus
            }
          });
          document.dispatchEvent(event);
        },
        (status) => {
          // Update status
          playlistTrack._downloadStatus = status;
          const event = new CustomEvent('downloadStatusChange', {
            detail: {
              trackId,
              status,
              preChecks: playlistTrack._preChecks,
              postChecks: playlistTrack._postChecks
            }
          });
          document.dispatchEvent(event);
        }
      );

      // Download succeeded - set audio buffer
      playlistTrack.audioBuffer = result.audioBuffer;
      playlistTrack._isDownloading = false;
      playlistTrack._downloadProgress = 100;
      playlistTrack._downloadStatus = 'DOWNLOADED';
      playlistTrack._preChecks = result.preChecks;
      playlistTrack._postChecks = result.postChecks;

      // Now start analysis pipeline
      playlistTrack._analyzing = true;
      playlistTrack._downloadStatus = 'ANALYZING';

      const event = new CustomEvent('downloadComplete', {
        detail: {
          trackId,
          audioBuffer: result.audioBuffer,
          preChecks: result.preChecks,
          postChecks: result.postChecks
        }
      });
      document.dispatchEvent(event);

      console.log(`✓ Download complete for ${searchResult.title}`);
    } catch (err) {
      console.error(`❌ Download failed for ${trackId}:`, err);

      // Update playlist track with error status
      playlistTrack._isDownloading = false;
      playlistTrack._downloadStatus = 'ERROR';
      playlistTrack._downloadError = err.message;

      const event = new CustomEvent('downloadFailed', {
        detail: {
          trackId,
          error: err.message
        }
      });
      document.dispatchEvent(event);
    } finally {
      this.downloadingTracks.delete(trackId);
    }
  }

  /**
   * Generate consistent gradient fallback for missing art
   */
  getGradientPlaceholder(sourceId) {
    // Create consistent colors from sourceId hash
    const hash = Array.from(sourceId).reduce((h, c) => h + c.charCodeAt(0), 0);
    const h1 = (hash * 137) % 360;
    const h2 = (h1 + 60) % 360;

    // Return SVG gradient (data URL)
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100">
      <defs>
        <radialGradient id="grad">
          <stop offset="0%" style="stop-color:hsl(${h1},70%,60%);stop-opacity:1" />
          <stop offset="100%" style="stop-color:hsl(${h2},70%,40%);stop-opacity:1" />
        </radialGradient>
      </defs>
      <rect width="100" height="100" fill="url(#grad)"/>
    </svg>`;

    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }

  /**
   * Escape HTML to prevent injection
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

/**
 * Initialize search UI for both decks
 */
export function initializeSearchUI(storage, downloadManager) {
  new SearchUI('A', storage, downloadManager);
  new SearchUI('B', storage, downloadManager);

  // Clean old search results periodically
  setInterval(() => {
    storage.clearOldSearchResults().catch(err => {
      console.warn('Failed to clear old search results:', err);
    });
  }, 60 * 60 * 1000); // Every hour
}
